import { useMemo, type ComponentProps } from "react";
import { DocumentSection } from "./DocumentSection";
import { LegacyHistoricalHeader } from "./LegacyHistoricalHeader";
import { LegacyHistoricalText } from "./LegacyHistoricalText";
import { LegacyHistoricalGraphic } from "./LegacyHistoricalGraphic";
import { LegacyHistoricalSignature } from "./LegacyHistoricalSignature";
import { LegacyHistoricalPhotos } from "./LegacyHistoricalPhotos";
import { LegacyHistoricalReportUnavailable } from "./LegacyHistoricalReportUnavailable";
import { hasCompleteHistoricalSourceHeader } from "./legacyHistoricalReportLayout";

export function ReportDocumentSection(
  props: ComponentProps<typeof DocumentSection>
) {
  const { section, documentViewData, pageIndex, reportPhotos } = props;
  const baseline = documentViewData.legacyBaseline;
  const block = section.config?.legacyBlock;
  const signature = useMemo(
    () =>
      block?.kind === "signature"
        ? baseline?.signatures.find(({ id }) => id === block.signatureId)
        : undefined,
    [baseline, block]
  );
  const asset = useMemo(
    () =>
      block?.kind === "organ_map"
        ? baseline?.organMaps.find(({ id }) => id === block.assetId)
        : undefined,
    [baseline, block]
  );
  const useSourceHeader = useMemo(
    () => Boolean(baseline && hasCompleteHistoricalSourceHeader(baseline)),
    [baseline]
  );
  if (!baseline) return <DocumentSection {...props} />;
  if (
    section.type === "header" &&
    section.config?.legacyUseSourceHeader &&
    !pageIndex &&
    useSourceHeader &&
    !documentViewData.legacyReportHeader
  )
    return null;
  if (section.type === "header")
    return (
      <LegacyHistoricalHeader
        header={documentViewData.legacyReportHeader ?? baseline.header}
        continuation={Boolean(pageIndex)}
      />
    );
  if (section.type === "diagnostic_map" || section.type === "organ_map")
    return (
      <LegacyHistoricalPhotos
        photos={reportPhotos || []}
        rowIndex={section.config?.mediaRowIndex}
      />
    );
  if (!block) return <DocumentSection {...props} />;
  if (block.kind === "narrative")
    return (
      <LegacyHistoricalText
        text={block.text}
        label={
          block.sourceKind === "source_header" &&
          (documentViewData.legacyReportHeader || !useSourceHeader)
            ? "Original source header (historical)"
            : section.label
        }
        sectionId={block.sectionId}
      />
    );
  if (block.kind === "signature") {
    return signature ? (
      <LegacyHistoricalSignature
        signature={signature}
        assets={documentViewData.legacyBaselineAssets}
      />
    ) : (
      <LegacyHistoricalReportUnavailable />
    );
  }
  return asset ? (
    <LegacyHistoricalGraphic
      asset={asset}
      assets={documentViewData.legacyBaselineAssets}
      description="Original organ map with its source labels and legend"
    />
  ) : (
    <LegacyHistoricalReportUnavailable />
  );
}
